/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// react and redux
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useOutletContext,
  useParams,
  useLocation,
  Outlet
} from 'react-router-dom';
// actions
import {
  getInstanceCommentsCounts,
  getInstanceIMICommentsCounts
} from '../../../actions';
// store
import { updateNavigationItem } from '../../../store/BASE/navigationSlice';
import { motion } from 'framer-motion';
// BASE components
import BASEPageSimple from '../../../../@BASE/core/BASEPageSimple';
import BASELoading from '../../../../@BASE/core/BASELoading';
import BASESvgIcon from '../../../../@BASE/core/BASESvgIcon';
import useThemeMediaQuery from '../../../../@BASE/hooks/useThemeMediaQuery';
// material-ui components
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
// custom components
import InstanceSidebar from '../../../shared-components/crud/instance/InstanceSidebar';
import LessonStatusSteps from './LessonStatusSteps';
import StatusCard from '../../../shared-components/cards/StatusCard';
import ChildListingHeader from '../../../shared-components/crud/listing/ChildListingHeader';
import InstanceList from '../../../shared-components/crud/listing/InstanceList';
import CommentCard from '../../../shared-components/cards/CommentCard';
import VersioningCard from '../../../shared-components/cards/VersioningCard';
import ReviewsCard from '../../../shared-components/cards/ReviewsCard';
import ExportCard from '../../../shared-components/cards/ExportCard';

const Root = styled(BASEPageSimple)(({ theme }) => ({
  '& .BASEPageSimple-content': {},
  '& .BASEPageSimple-sidebarHeader': {},
  '& .BASEPageSimple-sidebarContent': {}
}));

const container = {
  show: {
    transition: {
      staggerChildren: 0.04
    }
  }
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const LessonView = () => {
  const { instance, loading } = useOutletContext();

  const dispatch = useDispatch();
  const location = useLocation();
  const routeParams = useParams();
  const pageLayout = useRef(null);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [commentSummary, setCommentSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0
  });
  const [productionCommentSummary, setProductionCommentSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0
  });
  const getCommentCount = async () => {
    try {
      const countResponse = await dispatch(
        getInstanceCommentsCounts('lessons')({
          id: routeParams.id,
          showInternal: 'external'
        })
      ).unwrap();
      setCommentSummary(countResponse);
    } catch (err) {
      console.log(err);
    }
  };

  const getProductionCommentCount = async () => {
    try {
      const countResponse = await dispatch(
        getInstanceIMICommentsCounts('lessons')({
          id: routeParams.id,
          showInternal: 'external'
        })
      ).unwrap();
      setProductionCommentSummary(countResponse);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (
      location.pathname.includes('/status') ||
      location.pathname.includes('/version') ||
      location.pathname.includes('/export')
    ) {
      setRightSidebarOpen(true);
    } else {
      setRightSidebarOpen(false);
    }
    // getCommentCount();
    // getProductionCommentCount();
  }, [location.pathname]);

  useEffect(() => {
    if (instance && !loading) {
      getCommentCount();
      getProductionCommentCount();
    }

    if (instance) {
      dispatch(
        updateNavigationItem('lesson-component', {
          url: `lessons/${routeParams.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('course-component', {
          url: `courses/${instance?.course?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('section-component', {
          url: `sections`,
          disabled: true
        })
      );
      dispatch(
        updateNavigationItem('page-component', {
          url: `pages`,
          disabled: true
        })
      );
    }
  }, [dispatch, instance]);

  // if lesson is null return loading
  if (!instance || loading) {
    return <BASELoading />;
  }

  // return lesson view
  return (
    <Root
      content={
        <div className="w-full my-64 px-24 sm:px-24">
          <motion.div
            className="w-full"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-32 w-full">
              <motion.div
                className="col-span-1 lg:col-span-2 flex flex-col flex-auto space-y-16"
                variants={item}
              >
                <ChildListingHeader
                  resource="sections"
                  subtitle="Learning Objectives"
                  parent={instance.id}
                />
                <Divider />
                <InstanceList
                  listing={instance.sections}
                  resource="sections"
                />
              </motion.div>
              <motion.div variants={item} className="flex flex-col flex-auto">
                <StatusCard
                  instance={instance}
                  steps={LessonStatusSteps}
                  resource="Lesson"
                />
                <CommentCard
                  commentSummary={commentSummary}
                  productionCommentSummary={productionCommentSummary}
                  resource="Lesson"
                />
                <ReviewsCard reviews={instance?.reviews} resource="Lesson" />
                <VersioningCard instance={instance} steps={LessonStatusSteps} />
                <ExportCard />
              </motion.div>
            </div>
          </motion.div>
        </div>
      }
      ref={pageLayout}
      rightSidebarContent={
        <InstanceSidebar resource="lessons" instance={instance} />
      }
      rightSidebarOpen={rightSidebarOpen}
      handleRightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={400}
      rightSidebarVariant="temporary"
      scroll={isMobile ? 'normal' : 'normal'}
    />
  );
};

export default LessonView;
